import { useCallback, useContext, useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { type Chat, ChatActionType, type ChatResponseType, type HCPResultV2 } from '@trinity/chat';
import { Table } from '@trinity/chat-components/components/Chat.Attachments/Table';
import {
  CancelAction,
  TextQueryHint,
} from '@trinity/chat-components/components/presentation/Message.QueryHint/index.js';
import { SubmitActionContext } from './context';
import { EmailMailToLink, OpenAddressCellLink } from './LinkCells';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { MessageContainer } from './Message.System.Wrapper';

type Props = {
  queryIdentifier: string;
  response: Chat.QueryResponse<typeof ChatResponseType.HcpSearchResults>;
  disabled?: boolean;
};

export const HcpSearchResultsResponse = ({ queryIdentifier, response, disabled = false }: Props) => {
  const submitAction = useContext(SubmitActionContext);

  const hasResults = response.data.results.length > 0;
  const showPrimaryAffiliationColumn = useMemo(() => {
    return response.data.results.some((result) => {
      return result.primaryAffiliation;
    });
  }, [response.data.results]);

  const showPhoneColumn = useMemo(() => {
    return response.data.results.some(
      (result) => typeof result.phone === 'string' && result.phone.trim().length > 0
    );
  }, [response.data.results]);

  const handleSelectProfile = useCallback(
    (item: { id: number | string; name: string }) => {
      if (disabled) {
        return;
      }

      submitAction(
        {
          type: ChatActionType.SelectHcpSubject,
          payload: {
            hcpId: item.id,
            hcpName: item.name,
          },
        },
        `Show me the profile for ${item.name}.`
      );
    },
    [disabled, submitAction]
  );

  const columns = useMemo<GridColDef[]>(() => {
    const tableColumns: GridColDef[] = [
      // { field: 'id', headerName: 'ID', type: 'string', minWidth: 90, maxWidth: 120 },
      { field: 'name', headerName: 'Name', type: 'string', minWidth: 190, maxWidth: 300 },
      {
        field: 'profile',
        headerName: 'Profile',
        type: 'string',
        minWidth: 105,
        maxWidth: 105,
        sortable: false,
        renderCell: (params: GridRenderCellParams<HCPResultV2>) => {
          const id = params.row.id;
          const name = params.row.name;

          return (
            <StyledSelectButton
              disabled={disabled}
              onClick={() => {
                handleSelectProfile({ id, name });
              }}
              value="View"
            />
          );
        },
      },
      {
        field: 'location',
        headerName: 'Location',
        type: 'string',
        minWidth: 220,
        maxWidth: 360,
        sortable: false,
        renderCell: (params: GridRenderCellParams<HCPResultV2>) => {
          if (!params.row.location) {
            return null;
          }

          return <OpenAddressCellLink address={params.row.location} />;
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        type: 'string',
        minWidth: 220,
        maxWidth: 340,
        renderCell: (params: GridRenderCellParams<HCPResultV2>) => {
          const email = params.row.email?.trim();
          if (!email) {
            return null;
          }

          return <EmailMailToLink email={email} />;
        },
      },
    ];

    if (showPrimaryAffiliationColumn) {
      tableColumns.splice(2, 0, {
        field: 'primaryAffiliation',
        headerName: 'Primary Affiliation',
        type: 'string',
        minWidth: 220,
        maxWidth: 320,
      });
    }

    if (showPhoneColumn) {
      tableColumns.push({
        field: 'phone',
        headerName: 'Phone',
        type: 'string',
        minWidth: 150,
        maxWidth: 220,
      });
    }

    return tableColumns;
  }, [disabled, handleSelectProfile, showPhoneColumn, showPrimaryAffiliationColumn]);

  const handleStartHcpSearch = useCallback(() => {
    if (disabled) {
      return;
    }

    submitAction(
      {
        type: ChatActionType.StartHcpSearch,
      },
      'Search HCPs.'
    );
  }, [disabled, submitAction]);

  return (
    <MessageContainer>
      <BasicSystemUserMessage bodyStyles={css({ maxWidth: '100%' })}>
        <Body>
          <Message>{buildMessageFilter(response.data.search)}</Message>
          {hasResults ? (
            <Table
              tableId={`hcp-search-results-${queryIdentifier}-${response.data.results.length}`}
              columns={columns}
              rows={response.data.results}
              title="hcp-search-results"
              showExportCsv={true}
            />
          ) : (
            <NoResults>No HCPs were found for your search criteria.</NoResults>
          )}

          <StyledCancelButton
            displayValue="Start a new search"
            onClick={handleStartHcpSearch}
            disabled={disabled}
          />
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

function buildMessageFilter(
  search: Chat.QueryResponse<typeof ChatResponseType.HcpSearchResults>['data']['search']
) {
  const filters: string[] = [];

  if (search.name) {
    filters.push(`matching the name "${search.name}"`);
  }

  if (search.location) {
    const locationLabel =
      search.location.name || `(${search.location.latitude}, ${search.location.longitude})`;
    filters.push(`near ${locationLabel}`);
  }

  if (!filters.length) {
    return 'Sure, I found the following HCPs.';
  }

  return `Sure, I found the following HCPs ${filters.join(' and ')}.`;
}

const StyledSelectButton = styled(TextQueryHint)({
  minWidth: 90,
  maxWidth: 90,
});

const StyledCancelButton = styled(CancelAction)({
  marginTop: 15,
});

const Body = styled.div({
  width: '100%',
});

const Message = styled.div({
  marginBottom: 15,
});

const NoResults = styled.div(({ theme }) => ({
  marginTop: 10,
  fontFamily: theme.fonts.regular,
}));
